import { Volume } from '@/api/type-server'
import { Progress, Trans } from '@/components'
import { useUserStore } from '@/stores/user'
import { mb2GbString, toGbStrig } from '@/utils/file'
import { cn } from '@/utils/style'

interface VolumeSectionProps {
  volumes: Volume[]
}

export default function SnapVolumeSection({ volumes }: VolumeSectionProps) {
  const userStore = useUserStore()

  if (volumes.length === 0) {
    volumes[0] = {
      usage_mb: 0,
      quota_gb: 0,
      init_quota_gb: 0,
      last_quota_gb: 0,
      type: 2,
      domain: 0,
      id: -1,
    }
  }

  const isExpired = userStore.isOrderExpired

  return (
    <div>
      <h3 className="t-sb text-primary mb-4">
        <Trans i18nKey="space.volumeItemTitleSnapshot">Snapshot</Trans>
      </h3>
      <div className="gap-4">
        {volumes.map((volume) => {
          const effectiveQuota = isExpired ? volume.last_quota_gb : volume.quota_gb
          const usage = effectiveQuota > 0 ? volume.usage_mb / 1024 / effectiveQuota : 0
          return (
            <div
              key={`volume.${volume.id}`}
              className="w-full p-6 flex rounded-2xl gap-3 items-start border border-gray-300"
            >
              <div className="w-[120px]">
                <div className={cn(' text-[20px] leading-7 font-bold')}>
                  {`${effectiveQuota ? toGbStrig(effectiveQuota) : '-'}`}
                </div>
                <div className=" t-sm text-secondary">
                  <Trans i18nKey="space.volumeItemSubTitleStorage">Storage</Trans>
                </div>
              </div>
              <div className="w-full">
                <div className="py-[10px]">
                  <Progress
                    color={cn({
                      'bg-tertiary': isExpired,
                      'bg-error': !isExpired && usage === 1,
                      'bg-warning': !isExpired && usage >= 0.85 && usage < 1,
                      'bg-btn-secondary': !isExpired && usage < 0.85,
                    })}
                    value={usage}
                  />
                </div>

                {volume && effectiveQuota > 0 && (
                  <div className="flex gap-x-1 t-sm">
                    <>
                      <Trans
                        i18nKey="space.volumeItemUsage"
                        values={{
                          usage: mb2GbString(volume.usage_mb),
                          quota: toGbStrig(effectiveQuota),
                        }}
                        components={[
                          <span className="t-sm text-secondary">Using </span>,
                          <span className="t-sm text-secondary"> of </span>,
                        ]}
                      >
                        Using {{ usage: usage == 1 ? usage : mb2GbString(volume.usage_mb) }}GiB of{' '}
                        {{ quota: toGbStrig(effectiveQuota) }}GiB
                      </Trans>

                      <div className="t-sm text-tertiary">
                        &nbsp;&nbsp;{`${(usage * 100).toFixed(2) || '0'}`}%
                      </div>
                    </>
                  </div>
                )}

                {usage >= 0.85 && usage < 1 && (
                  <div className="flex gap-x-1 t-sm text-warning">
                    <Trans i18nKey="space.volumeItemLowStorage">
                      Low storage – manage your storage to avoid interruption
                    </Trans>
                  </div>
                )}

                {(!volume || effectiveQuota <= 0 || usage >= 1) && (
                  <div className={cn('t-sm ', usage >= 1 ? 'text-error' : 'text-tertiary')}>
                    <Trans i18nKey="space.volumeItemNotAvailable">
                      No storage available – adjust your quota to continue
                    </Trans>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
